import React, { useEffect, useState, useRef, useLayoutEffect, lazy, Component } from 'react';
import ReactDOM from 'react-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { Link, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { ArrowLeft, ArrowRight, ArrowDown, Image, CheckCircle, X, MagnifyingGlassPlus, CaretLeft, CaretRight, TextT, Layout, Compass, Database, Lightning, Target, Rocket, WarningCircle, Users, Eye, FileText, MagnifyingGlass, Lightbulb, Clock, UserCheck, TrendUp, Gear, BookOpen, ChatCircle, NavigationArrow, Stack, PencilSimple, Info } from '@phosphor-icons/react';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
// Optimized Image Component
function SimpleImage({
  src,
  alt,
  className = ''
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  return <div className={`overflow-hidden rounded-lg relative ${className}`} style={{
    contain: 'content'
  }} data-id="element-572">
      {!isLoaded && <div className="w-full h-full bg-gray-100 absolute inset-0 animate-pulse" data-id="element-573" />}
      <img src={src} alt={alt} loading="lazy" decoding="async" onLoad={() => setIsLoaded(true)} className={`w-full h-auto transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} data-id="element-574" />
    </div>;
}
// Modal Portal Component
function ModalPortal({
  children,
  isOpen
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || !isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
}
// Lightbox Image Component
function LightboxImage({
  src,
  alt,
  label
}: {
  src: string;
  alt: string;
  label?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <>
      <div className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-50 border border-gray-100" onClick={() => setIsOpen(true)} role="button" tabIndex={0} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
    }} data-id="element-575">
        {!isLoaded && <div className="w-full aspect-video bg-gray-100 animate-pulse" data-id="element-576" />}
        <img src={src} alt={alt} loading="lazy" decoding="async" onLoad={() => setIsLoaded(true)} className={`w-full h-auto transition-all duration-500 ${isLoaded ? 'opacity-100 group-hover:scale-[1.02]' : 'opacity-0'}`} data-id="element-577" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center" data-id="element-578">
          <div className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300" data-id="element-579">
            <div className="bg-white text-[#003071] px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2" data-id="element-580">
              <MagnifyingGlassPlus className="w-4 h-4" data-id="element-581" />
              Zoom
            </div>
          </div>
        </div>
      </div>

      <ModalPortal isOpen={isOpen} data-id="element-582">
        <div onClick={() => setIsOpen(false)} className="fixed inset-0 bg-white/95 z-[99999] flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm" style={{
        isolation: 'isolate'
      }} data-id="element-583">
          <button onClick={e => {
          e.stopPropagation();
          setIsOpen(false);
        }} className="absolute top-6 right-6 p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10" aria-label="Close lightbox" data-id="element-584">
            <X className="w-6 h-6 text-black" data-id="element-585" />
          </button>

          {label && <div className="absolute top-8 left-8 text-black text-xl font-medium z-10" data-id="element-586">
              {label}
            </div>}

          <img src={src} alt={alt} onClick={e => e.stopPropagation()} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" style={{
          pointerEvents: 'none'
        }} data-id="element-587" />
        </div>
      </ModalPortal>
    </>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    src: string;
    alt: string;
    label: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };
  return <div className="relative w-full bg-gray-100 rounded-2xl overflow-hidden shadow-sm border border-gray-200" data-id="element-588">
      <div className="aspect-[16/9] relative" data-id="element-589">
        <img src={images[currentIndex].src} alt={images[currentIndex].alt} className="w-full h-full object-contain bg-gray-50" data-id="element-590" />
        <button onClick={prevSlide} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg transition-all backdrop-blur-sm" aria-label="Previous image" data-id="element-591">
          <CaretLeft className="w-4 h-4 sm:w-6 sm:h-6" data-id="element-592" />
        </button>
        <button onClick={nextSlide} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg transition-all backdrop-blur-sm" aria-label="Next image" data-id="element-593">
          <CaretRight className="w-4 h-4 sm:w-6 sm:h-6" data-id="element-594" />
        </button>
        <div className="absolute bottom-2 sm:bottom-6 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-md text-white px-3 py-1.5 sm:px-6 sm:py-2 rounded-full text-xs sm:text-sm font-medium max-w-[90%] text-center" data-id="element-595">
          {images[currentIndex].label}
        </div>
      </div>
      <div className="absolute bottom-2 sm:bottom-4 left-0 right-0 flex justify-center gap-1.5 sm:gap-2" data-id="element-596">
        {images.map((_, idx) => <button key={idx} onClick={() => setCurrentIndex(idx)} className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4 sm:w-6' : 'bg-white/50'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-597" />)}
      </div>
    </div>;
}
// Vertical Timeline Component
function VerticalTimeline() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const steps = [{
    number: '01',
    label: 'Step 1',
    title: 'User-Centric Validation',
    desc: 'Recurring sessions with faculty and staff to ensure every decision was validated through direct user input'
  }, {
    number: '02',
    label: 'Step 2',
    title: 'Rapid Iteration',
    desc: 'Leveraged AI tools during wireframing to accelerate concept generation and testing'
  }, {
    number: '03',
    label: 'Step 3',
    title: 'Parallel Innovation',
    desc: 'Improved core UX while developing AI-powered CV parsing with Python engineers'
  }, {
    number: '04',
    label: 'Step 4',
    title: 'Continuous Research',
    desc: 'Video documented feedback, used Dovetail AI for pattern recognition, synthesized through affinity diagramming'
  }, {
    number: '05',
    label: 'Step 5',
    title: 'Live Refinement',
    desc: 'Conducted sessions with engineers and stakeholders to transform technical constraints into design opportunities'
  }, {
    number: '06',
    label: 'Step 6',
    title: 'Strategic Partnerships',
    desc: 'Demonstrated working prototypes to secure Amazon partnership for pilot testing'
  }];
  return <div ref={containerRef} className="relative py-12 sm:py-20 ml-4 sm:ml-8 md:ml-12" data-id="element-598">
      {/* Vertical Line Background */}
      <div className="absolute left-[19px] sm:left-[27px] top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-599" />

      {/* Animated Vertical Line */}
      <motion.div className="absolute left-[19px] sm:left-[27px] top-0 w-0.5 bg-[#003071] origin-top" style={{
      scaleY,
      height: '100%'
    }} data-id="element-600" />

      <div className="space-y-16 sm:space-y-24" data-id="element-601">
        {steps.map((step, idx) => <TimelineStep key={idx} step={step} index={idx} data-id="element-602" />)}
      </div>
    </div>;
}
function TimelineStep({
  step,
  index
}: {
  step: {
    number: string;
    label: string;
    title: string;
    desc: string;
  };
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="relative flex items-start gap-8 sm:gap-12" data-id="element-603">
      {/* Number Circle */}
      <div className="relative z-10 flex-shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white border-2 border-[#003071] flex items-center justify-center text-[#003071] font-bold text-sm sm:text-lg shadow-sm" data-id="element-604">
        {step.number}
      </div>

      {/* Content Card */}
      <div className="flex-1 pt-1 sm:pt-2" data-id="element-605">
        <span className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-2 block" data-id="element-606">
          {step.label}
        </span>
        <h3 className="text-lg sm:text-xl font-medium text-[#003071] mb-2 sm:mb-3" data-id="element-607">
          {step.title}
        </h3>
        <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl" data-id="element-608">
          {step.desc}
        </p>
      </div>
    </motion.div>;
}
interface CaseStudyCaseWesternProps {
  isOverlay?: boolean;
}
export function CaseStudyCaseWestern({
  isOverlay = false
}: CaseStudyCaseWesternProps) {
  const {
    viewPreference,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  const {
    openChatDrawer,
    previousPage,
    setPreviousPage
  } = usePortfolio();
  // Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Navigate first
    navigate('/');
    // FIXED: Scroll to top AFTER navigation completes
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    // Observe all sections
    const sections = ['overview', 'objectives', 'discovery', 'process', 'results'];
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });
    return () => {
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 128; // Account for sticky header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'objectives',
    label: 'Objectives'
  }, {
    id: 'discovery',
    label: 'Problem'
  }, {
    id: 'process',
    label: 'Solution'
  }, {
    id: 'results',
    label: 'Impact'
  }];
  return <>
      {!isOverlay && <CustomCursor data-id="element-609" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-610">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-611">
          {!isOverlay && <CaseStudyHeader data-id="element-612" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-613" />}

          {/* Hero Section - Clean White Background */}
          <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full" data-id="element-614">
            <div className="max-w-4xl" data-id="element-615">
              {/* Back to Portfolio button - always visible */}
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#003071] transition-colors mb-6 sm:mb-8 group" aria-label="Back to portfolio" data-id="element-616">
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" data-id="element-617" />
                  <span className="text-sm font-medium" data-id="element-618">Back to Portfolio</span>
                </button>}

              <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-6 sm:mb-8 text-[#003071] leading-[1.1]" data-id="element-619">
                Faculty Information System Redesign
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-620">
                Redesigning a Faculty Profile & Activity Tracking System:
                Bridging Technical Complexity and Human-Centered Design
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden mb-16 sm:mb-24" data-id="element-621">
            <img src="https://cdn.magicpatterns.com/uploads/61dpXs15GKMAKBJcYfBiri/CWRU_COMPV.png" alt="Case Western Hero" className="w-full h-full object-cover object-center" data-id="element-622" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32" data-id="element-623">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-624">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-625">
                <div className="sticky top-32 space-y-6" data-id="element-626">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#003071] font-medium border-l-2 border-[#003071] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-627">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32 sm:space-y-40" data-id="element-628">
                {/* Overview Section */}
                <section id="overview" className="scroll-mt-32" data-id="element-629">
                  <div className="grid md:grid-cols-2 gap-16 mb-24" data-id="element-630">
                    <div data-id="element-631">
                      <h3 className="text-sm font-medium text-[#003071] uppercase tracking-wider mb-2" data-id="element-632">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-633">
                        Lead Product Designer
                      </p>
                    </div>
                    <div data-id="element-634">
                      <h3 className="text-sm font-medium text-[#003071] uppercase tracking-wider mb-2" data-id="element-635">
                        Project Duration
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-636">6 Months</p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#003071]" data-id="element-637">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-638">
                    Case Western Reserve University hired me for an initially
                    3-month contract to redesign their Faculty Information
                    System. The engagement extended to six months as we tackled
                    multiple parallel workstreams: navigation redesign, design
                    system customization, establishing UX best practices for
                    developer workflows, and exploring AI solutions to
                    streamline data entry.
                  </p>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-12" data-id="element-639">
                    <h3 className="text-xl font-medium mb-8 text-[#003071]" data-id="element-640">
                      Key Achievements
                    </h3>
                    <div className="space-y-4 mb-12" data-id="element-641">
                      {['Built faculty ownership through 16 bi-weekly interviews across colleges, fostering inclusive co-design that elevated stakeholder buy-in', 'Developed AI-powered proof of concept achieving 85%+ accuracy in CV parsing', 'Secured two-month pilot program with Amazon for real-world testing', 'Delivered three core improvements: standardized terminology, enhanced visual hierarchy, improved navigation'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-642">
                          <CheckCircle className="w-5 h-5 text-[#003071] mt-1 flex-shrink-0" data-id="element-643" />
                          <span className="text-slate-700 text-lg" data-id="element-644">{item}</span>
                        </div>)}
                    </div>

                    <div data-id="element-645">
                      <LightboxImage src="https://cdn.magicpatterns.com/uploads/b1CFgyJEco99E9Ys6agrnZ/Home_Page_FIS.png" alt="Faculty Information System home page interface" label="FIS Home Page" data-id="element-646" />
                    </div>
                  </div>
                </section>

                {/* Objectives Section */}
                <section id="objectives" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-647">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-648">
                    Objectives
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-649">
                    Transform faculty productivity while establishing a scalable
                    foundation for AI-driven innovation across the university.
                  </p>

                  <div className="space-y-3 sm:space-y-4 mb-24 sm:mb-32" data-id="element-650">
                    {[{
                    title: 'Faculty Productivity',
                    desc: 'Reduce annual review time from 3+ hours to minutes through intuitive workflows'
                  }, {
                    title: 'Institutional Efficiency',
                    desc: 'Create a unified research ecosystem eliminating fragmented data silos'
                  }, {
                    title: 'Future Innovation',
                    desc: 'Establish scalable foundation for AI-powered research tools'
                  }, {
                    title: 'User Adoption',
                    desc: 'Transform skepticism into ownership through collaborative design'
                  }, {
                    title: 'Enterprise Scalability',
                    desc: 'Balance rapid execution with long-term maintainability in a live production environment'
                  }].map((card, idx) => <div key={idx} className="border border-gray-200 p-4 sm:p-6 rounded-lg hover:border-gray-300 transition-colors" data-id="element-651">
                        <div className="flex items-start gap-4 sm:gap-5" data-id="element-652">
                          <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-[#003071] flex items-center justify-center text-xs font-bold text-[#003071]" data-id="element-653">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-654">
                            <h3 className="text-lg font-medium text-[#003071] mb-2" data-id="element-655">
                              {card.title}
                            </h3>
                            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-656">
                              {card.desc}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </section>

                {/* Discovery Section */}
                <section id="discovery" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-657">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-658">
                    Problem & Discovery
                  </h2>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-659">
                    The Faculty Information System serves as the university's
                    central platform for faculty profiles and activity tracking.
                    Each year, faculty spend 3+ hours on annual reviews
                    requiring extensive manual data entry. This hidden
                    institutional tax had persisted through seven years of
                    evolution without dedicated UX expertise.
                  </p>

                  <div className="mb-16 sm:mb-24 border-l-2 border-[#003071] pl-8 py-4" data-id="element-660">
                    <h3 className="text-xs font-bold text-[#003071] uppercase tracking-wider mb-6" data-id="element-661">
                      Problem Statement
                    </h3>
                    <p className="text-2xl font-light leading-relaxed text-slate-900 mb-0" data-id="element-662">
                      Through initial user interviews, we discovered that
                      technical complexity had become a barrier to entry.
                      Faculty were experiencing{' '}
                      <span className="font-medium text-[#003071]" data-id="element-663">
                        decision paralysis
                      </span>{' '}
                      from inconsistent terminology, poor information
                      architecture, and cluttered interfaces.
                    </p>
                  </div>

                  {/* Grouped Discovery Content */}
                  <div className="bg-gray-50 rounded-2xl p-8 sm:p-12 mb-16 sm:mb-24" data-id="element-664">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16" data-id="element-665">
                      {[{
                      icon: ChatCircle,
                      title: 'Terminology Confusion',
                      desc: 'Inconsistent language across schools created cognitive barriers'
                    }, {
                      icon: NavigationArrow,
                      title: 'Navigation Issues',
                      desc: 'Poor information architecture made finding information difficult'
                    }, {
                      icon: Stack,
                      title: 'Visual Hierarchy Problems',
                      desc: 'Cluttered pages lacking structure increased cognitive load'
                    }, {
                      icon: Clock,
                      title: 'Manual Data Entry Tax',
                      desc: 'Excessive manual workflows created institutional inefficiency'
                    }].map((item, idx) => <div key={idx} className="bg-white p-4 sm:p-5 rounded-xl border border-gray-100 shadow-sm" data-id="element-666">
                          <div className="flex items-start gap-3 sm:gap-4" data-id="element-667">
                            <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center" data-id="element-668">
                              <item.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#003071]" weight="thin" data-id="element-669" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-670">
                              <h4 className="font-medium text-lg mb-2 text-[#003071]" data-id="element-671">
                                {item.title}
                              </h4>
                              <p className="text-lg text-slate-600 leading-relaxed" data-id="element-672">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>

                    <div data-id="element-673">
                      <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/vRuKFVtSu1k26mpfBtXnY7/Before.png" alt="Legacy Faculty Information System interface" label="Before Redesign" data-id="element-674" />
                    </div>
                  </div>
                </section>

                {/* Process Section */}
                <section id="process" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-675">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-676">
                    Solution & Methodology
                  </h2>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-677">
                    I architected a dual-track strategy: prioritizing immediate
                    UX improvements while developing an AI proof of concept in
                    parallel. This approach delivered value quickly while
                    exploring transformative innovation.
                  </p>

                  {/* Methodology Framework */}
                  <div className="mb-24 sm:mb-32" data-id="element-678">
                    <h3 className="text-lg sm:text-xl font-medium mb-8 sm:mb-12 text-[#003071]" data-id="element-679">
                      Strategic Approach
                    </h3>
                    <VerticalTimeline data-id="element-680" />
                  </div>

                  <div className="space-y-24 sm:space-y-32 md:space-y-40" data-id="element-681">
                    {/* Step 1 */}
                    <div data-id="element-682">
                      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-683">
                        <span className="text-4xl sm:text-5xl font-light text-slate-300" data-id="element-684">
                          01
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-medium text-[#003071]" data-id="element-685">
                          Strategic Research & Discovery
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8 max-w-3xl" data-id="element-686">
                        With only 3 months initially budgeted, I made a
                        strategic decision: skip traditional lengthy research
                        phases and dive immediately into user interviews. I
                        designed a two-week sprint cycle with our PM, testing
                        with new faculty faces each iteration, maximizing
                        diverse input across 7 colleges while maintaining
                        velocity.
                      </p>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 sm:mb-12 max-w-3xl" data-id="element-687">
                        Quick competitive research revealed most universities
                        relied on costly third-party services. I saw an
                        opportunity: build an on-premise solution they could
                        white-label, creating valuable IP rather than renting
                        software.
                      </p>

                      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-688">
                        <div style={{
                        padding: '56.25% 0 0 0',
                        position: 'relative'
                      }} data-id="element-689">
                          <iframe src="https://player.vimeo.com/video/1142194486?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }} title="User interviews" data-id="element-690" />
                        </div>
                        <p className="text-sm text-gray-500 p-4 sm:p-6 text-center bg-gray-50 border-t border-gray-100" data-id="element-691">
                          Strategic user interviews capturing product sentiment
                          and pain points
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div data-id="element-692">
                      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-693">
                        <span className="text-4xl sm:text-5xl font-light text-slate-300" data-id="element-694">
                          02
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-medium text-[#003071]" data-id="element-695">
                          Rapid Prototyping & Technical Translation
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8 max-w-3xl" data-id="element-696">
                        I applied an AI-era approach to innovation that replaces
                        linear design processes with exponential concept
                        development. I used AI tools to rapidly generate and
                        test multiple design hypotheses simultaneously,
                        achieving in weeks what would traditionally take months.
                      </p>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 sm:mb-12 max-w-3xl" data-id="element-697">
                        I conducted live prototyping workshops where I helped
                        faculty visualize their ideas in real-time, saving each
                        version and using AI to synthesize them into a mean
                        design. This AI-assisted approach found the statistical
                        "mean" that satisfied diverse stakeholder needs,
                        achieving UI consensus in 10 weeks.
                      </p>

                      {/* Grouped Process Content */}
                      <div className="bg-gray-50 rounded-2xl p-8 sm:p-12" data-id="element-698">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12" data-id="element-699">
                          <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/5KpMMuX7A152SY5E7yEEkP/Ideation_1.png" alt="Navigation iteration 1" label="Iteration 1" data-id="element-700" />
                          <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/fZ3J5Y9Kes9FYqDKGquNRG/Ideation_2.png" alt="Navigation iteration 2" label="Iteration 2" data-id="element-701" />
                          <LightboxImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/sAd42ZLH2mUmRLh4anJCPC/Ideation_3.png" alt="Navigation iteration 3" label="Iteration 3" data-id="element-702" />
                        </div>

                        <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-703">
                          <div style={{
                          padding: '56.25% 0 0 0',
                          position: 'relative'
                        }} data-id="element-704">
                            <iframe src="https://player.vimeo.com/video/1142229645?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%'
                          }} title="Collaboration sessions" data-id="element-705" />
                          </div>
                          <p className="text-sm text-gray-500 p-4 sm:p-6 text-center bg-gray-50 border-t border-gray-100" data-id="element-706">
                            Live collaborative design sessions with engineers
                            and stakeholders
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div data-id="element-707">
                      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-708">
                        <span className="text-4xl sm:text-5xl font-light text-slate-300" data-id="element-709">
                          03
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-medium text-[#003071]" data-id="element-710">
                          AI Innovation & Strategic Partnerships
                        </h3>
                      </div>

                      <p className="text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8 max-w-3xl" data-id="element-711">
                        As both designer and inventor, I saw an opportunity to
                        create IP rather than rent software. Drawing on my
                        Purdue AI program coursework, I dove into the codebase
                        to architect a custom tagging and labeling system for
                        the FIS's complex infrastructure.
                      </p>

                      <p className="text-lg text-slate-600 leading-relaxed mb-8 sm:mb-12 max-w-3xl" data-id="element-712">
                        I created a unified tagging taxonomy that mapped three
                        disparate systems into a single data flow. I handed
                        Python engineers working code snippets that connected
                        the dots on our data strategy. The proof of concept
                        worked: a 36-page CV parsed in seconds, extracting
                        information into our desired format, presenting it for
                        user editing, and populating backend data tables.
                      </p>

                      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-713">
                        <div style={{
                        padding: '52.31% 0 0 0',
                        position: 'relative'
                      }} data-id="element-714">
                          <iframe src="https://player.vimeo.com/video/1148647481?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }} title="Prototype demo" data-id="element-715" />
                        </div>
                        <p className="text-sm text-gray-500 p-4 sm:p-6 text-center bg-gray-50 border-t border-gray-100" data-id="element-716">
                          First working prototype demonstrating real-time
                          AI-powered CV parsing
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results Section */}
                <section id="results" className="scroll-mt-24 sm:scroll-mt-32" data-id="element-717">
                  <h2 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12 text-[#003071]" data-id="element-718">
                    Business Impact & Outcomes
                  </h2>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-719">
                    The redesigned Faculty Information System launched to
                    positive feedback from both users and stakeholders. What
                    started as a fragmented system became a model of
                    human-centered design that established the foundation for
                    AI-driven innovation across the university. We delivered
                    three core improvements: standardized language that
                    eliminated cross-departmental confusion, enhanced
                    information architecture that reduced cognitive load through
                    strategic content hierarchy, and intuitive navigation that
                    aligned system structure with user mental models.
                  </p>

                  <div className="mb-16 sm:mb-24" data-id="element-720">
                    <ImageCarousel images={[{
                    src: 'https://cdn.magicpatterns.com/uploads/irjGEK8QTQH2hohHTgFZ4f/CWRU_COMPV.png',
                    alt: 'Interface improvements overview',
                    label: 'Interface Improvements'
                  }, {
                    src: 'https://cdn.magicpatterns.com/uploads/b1CFgyJEco99E9Ys6agrnZ/Home_Page_FIS.png',
                    alt: 'FIS home page',
                    label: 'FIS Home Page'
                  }, {
                    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vRuKFVtSu1k26mpfBtXnY7/Before.png',
                    alt: 'Before redesign',
                    label: 'Before Redesign'
                  }]} data-id="element-721" />
                  </div>

                  <div className="bg-[#003071] text-white p-8 sm:p-12 rounded-xl sm:rounded-2xl relative mb-16 sm:mb-24" data-id="element-722">
                    <div className="flex items-center gap-4 mb-8 sm:mb-12" data-id="element-723">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 rounded-xl flex items-center justify-center" data-id="element-724">
                        <Lightning className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-725" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-light" data-id="element-726">
                        AI-Powered Innovation
                      </h3>
                    </div>

                    <div className="mb-8 sm:mb-12" data-id="element-727">
                      <p className="text-lg leading-relaxed text-white/90 mb-6 sm:mb-8 max-w-3xl" data-id="element-728">
                        Collaborating with Python engineers, I helped architect
                        a proof of concept that leveraged AI to automatically
                        parse professors' CVs and populate annual review data,
                        translating complex technical requirements into
                        intuitive automation.
                      </p>
                      <p className="text-lg leading-relaxed text-white/90 max-w-3xl" data-id="element-729">
                        We engineered a "Data Slingshot," a custom taxonomy and
                        field-mapping strategy with strict logic "buckets" for
                        extracted data. This approach enabled accurate parsing
                        of complex publication lists, transforming hours of
                        manual entry into seconds of automated intelligence.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-730">
                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 relative group flex flex-col justify-between h-full" data-id="element-731">
                        <div className="flex items-start justify-between mb-4" data-id="element-732">
                          <span className="block text-3xl sm:text-4xl font-bold" data-id="element-733">
                            85%+
                          </span>
                          <div className="relative" data-id="element-734">
                            <Info className="w-5 h-5 sm:w-6 sm:h-6 text-white/60 cursor-help" data-id="element-735" />
                            <div className="absolute bottom-full right-0 mb-2 w-64 sm:w-72 bg-white text-slate-700 p-4 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 text-sm leading-relaxed pointer-events-none z-10" data-id="element-736">
                              Accuracy rate for automatically parsing
                              publications from faculty CVs and mapping them to
                              specific FIS form fields, eliminating manual data
                              entry for this section.
                              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white" data-id="element-737"></div>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-738">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-739">
                            Parsing Accuracy
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-740">
                            Achieved in initial proof of concept
                          </p>
                        </div>
                      </div>

                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-741">
                        <div className="mb-4" data-id="element-742">
                          <span className="block text-3xl sm:text-4xl font-bold" data-id="element-743">
                            Seconds
                          </span>
                        </div>
                        <div data-id="element-744">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-745">
                            Processing Time
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-746">
                            Reduced from 30+ minutes per CV
                          </p>
                        </div>
                      </div>

                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-747">
                        <div className="mb-4" data-id="element-748">
                          <Rocket className="w-8 h-8 sm:w-10 sm:h-10 text-white/80" data-id="element-749" />
                        </div>
                        <div data-id="element-750">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-751">
                            Amazon Pilot Program
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-752">
                            Secured two-month pilot for real-world testing
                          </p>
                        </div>
                      </div>

                      <div className="bg-white/10 p-6 sm:p-8 rounded-xl border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col justify-between h-full" data-id="element-753">
                        <div className="mb-4" data-id="element-754">
                          <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white/80" data-id="element-755" />
                        </div>
                        <div data-id="element-756">
                          <h4 className="text-lg sm:text-xl font-bold mb-2" data-id="element-757">
                            2026 Implementation
                          </h4>
                          <p className="text-sm sm:text-base text-white/70 leading-relaxed" data-id="element-758">
                            Full implementation scheduled for 2026
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-16 sm:mb-24 max-w-3xl" data-id="element-759">
                    Before my departure, stakeholders highlighted these
                    achievements in university presentations as the system
                    continues its phased rollout. Faculty and staff expressed
                    their appreciation for my push for innovation and
                    user-centered design. Understanding budget constraints
                    prevented hiring a designer post-departure, I trained two
                    team members on UX and UI design best practices, ensuring
                    the project's continued success and empowering the team to
                    maintain design quality as the system scales.
                  </p>

                  <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-8 sm:p-12 border border-gray-100" data-id="element-760">
                    <h3 className="text-xl sm:text-2xl font-medium mb-6 sm:mb-8 text-[#003071]" data-id="element-761">
                      Key Learnings
                    </h3>
                    <div className="space-y-6 sm:space-y-8" data-id="element-762">
                      <div data-id="element-763">
                        <h4 className="font-semibold text-slate-900 mb-2 sm:mb-3 text-lg" data-id="element-764">
                          Strategic Time Constraints Drive Innovation
                        </h4>
                        <p className="text-slate-600 leading-relaxed text-base sm:text-lg" data-id="element-765">
                          The 3-month timeline forced me to abandon traditional
                          research phases and embrace the Stingray methodology.
                          This constraint became an advantage, enabling rapid
                          iteration and AI-assisted design synthesis.
                        </p>
                      </div>
                      <div data-id="element-766">
                        <h4 className="font-semibold text-slate-900 mb-2 sm:mb-3 text-lg" data-id="element-767">
                          Technical Skills Amplify Design Impact
                        </h4>
                        <p className="text-slate-600 leading-relaxed text-base sm:text-lg" data-id="element-768">
                          My Purdue AI program training enabled me to bridge
                          design and engineering, delivering working Python code
                          alongside design specs. This technical fluency
                          accelerated development and proved concepts before
                          significant engineering investment.
                        </p>
                      </div>
                      <div data-id="element-769">
                        <h4 className="font-semibold text-slate-900 mb-2 sm:mb-3 text-lg" data-id="element-770">
                          Knowledge Transfer Ensures Longevity
                        </h4>
                        <p className="text-slate-600 leading-relaxed text-base sm:text-lg" data-id="element-771">
                          Training team members on UX best practices proved as
                          valuable as the designs themselves. Empowering the
                          team to maintain design quality post-departure ensured
                          the project's continued success and scalability.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} nextCase={{
          category: 'Case Studies',
          title: 'U.S. Bank',
          slug: 'us-bank'
        }} data-id="element-772" />}

          {!isOverlay && <Footer data-id="element-773" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-774" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-775" />}
    </>;
}